
import type { Marker, SlamJson, SlamPoint } from './types'

export function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n))
}

type DrawContext = {
    ctx: CanvasRenderingContext2D
    width: number
    height: number
    slamToScreenPx: (pt: SlamPoint, w: number, h: number) => { x: number; y: number }
    pxToCtx: (val: number) => number
}

export const drawObstacles = (
    { ctx, width, height, slamToScreenPx, pxToCtx }: DrawContext,
    normalizedObstacles: SlamPoint[],
    pathRef: React.MutableRefObject<Path2D | null>
) => {
    if (normalizedObstacles.length === 0) return

    const path = new Path2D()
    const firstPx = slamToScreenPx(normalizedObstacles[0], width, height)
    path.moveTo(firstPx.x, firstPx.y)

    for (let i = 1; i < normalizedObstacles.length; i++) {
        const px = slamToScreenPx(normalizedObstacles[i], width, height)
        path.lineTo(px.x, px.y)
    }
    path.closePath()
    pathRef.current = path

    ctx.fillStyle = '#E8F5E9'
    ctx.fill(path)
    ctx.strokeStyle = '#D98B64'
    ctx.lineWidth = pxToCtx(1)
    ctx.stroke(path)

    ctx.fillStyle = '#CE7A58'
    ctx.globalAlpha = 0.55
    for (let i = 0; i < normalizedObstacles.length; i += 5) {
        const px = slamToScreenPx(normalizedObstacles[i], width, height)
        ctx.beginPath()
        ctx.arc(px.x, px.y, pxToCtx(1.2), 0, Math.PI * 2)
        ctx.fill()
    }
    ctx.globalAlpha = 1
}

export const drawGridAndOrigin = (
    { ctx, width, height, slamToScreenPx, pxToCtx }: DrawContext
) => {
    const originPx = slamToScreenPx({ x: 0, y: 0 }, width, height)
    ctx.save()
    ctx.strokeStyle = '#cbd5e1'
    ctx.lineWidth = pxToCtx(1)
    ctx.setLineDash([pxToCtx(4), pxToCtx(4)])

    ctx.beginPath()
    ctx.moveTo(originPx.x, 0)
    ctx.lineTo(originPx.x, height)
    ctx.stroke()

    ctx.beginPath()
    ctx.moveTo(0, originPx.y)
    ctx.lineTo(width, originPx.y)
    ctx.stroke()

    ctx.setLineDash([])
    ctx.fillStyle = '#ef4444'
    ctx.beginPath()
    ctx.arc(originPx.x, originPx.y, pxToCtx(3), 0, Math.PI * 2)
    ctx.fill()

    ctx.fillStyle = '#64748b'
    ctx.font = `${pxToCtx(10)}px sans-serif`
    ctx.fillText('(0,0)', originPx.x + pxToCtx(5), originPx.y - pxToCtx(5))
    ctx.restore()
}

export const drawRobot = (
    { ctx, width, height, slamToScreenPx, pxToCtx }: DrawContext,
    robot: (NonNullable<SlamJson['robot']> & { yawRad: number }) | null
) => {
    if (!robot) return
    const base = slamToScreenPx({ x: robot.x ?? 0, y: robot.y ?? 0 }, width, height)
    const radius = pxToCtx(10)

    ctx.save()
    ctx.translate(base.x, base.y)
    ctx.rotate(robot.yawRad - Math.PI / 2)

    ctx.fillStyle = '#D98B64'
    ctx.strokeStyle = '#A85D3D'
    ctx.lineWidth = pxToCtx(1)
    ctx.beginPath()
    ctx.arc(0, 0, radius, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()

    ctx.fillStyle = '#ffffff'
    ctx.beginPath()
    ctx.moveTo(radius * 0.8, 0)
    ctx.lineTo(-radius * 0.4, -radius * 0.5)
    ctx.lineTo(-radius * 0.1, 0)
    ctx.lineTo(-radius * 0.4, radius * 0.5)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
}

export const drawHome = (
    { ctx, width, height, slamToScreenPx, pxToCtx }: DrawContext,
    home: (NonNullable<SlamJson['home']> & { yawRad: number }) | null,
    overlay?: { isSelected: boolean; showDirectionOverlay: boolean; effectiveYaw: number }
) => {
    if (!home) return
    const base = slamToScreenPx({ x: home.x ?? 0, y: home.y ?? 0 }, width, height)
    const houseWidth = pxToCtx(16)
    const houseHeight = pxToCtx(16)
    const roofHeight = pxToCtx(3)

    const bodyLeft = base.x - houseWidth / 2
    const bodyTop = base.y - houseHeight / 2
    const bodyRight = bodyLeft + houseWidth
    const bodyBottom = bodyTop + houseHeight

    ctx.save()
    ctx.fillStyle = '#d99271'
    ctx.strokeStyle = '#cf8260'
    ctx.lineWidth = pxToCtx(1)
    ctx.lineJoin = 'round'

    ctx.beginPath()
    ctx.moveTo(bodyLeft + pxToCtx(1.5), bodyTop + pxToCtx(4))
    ctx.lineTo(base.x, bodyTop - roofHeight)
    ctx.lineTo(bodyRight - pxToCtx(1.5), bodyTop + pxToCtx(4))
    ctx.lineTo(bodyRight, bodyBottom)
    ctx.lineTo(bodyLeft, bodyBottom)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()

    ctx.fillStyle = '#ffffff'
    ctx.beginPath()
    ctx.rect(
        base.x - houseWidth * 0.25,
        bodyTop + houseHeight * 0.4,
        houseWidth * 0.5,
        houseHeight * 0.6
    )
    ctx.fill()
    ctx.restore()

    if (overlay?.showDirectionOverlay) {
        // Vòng donut + mũi tên chỉ hướng, cùng bán kính với vùng bắt điểm xoay của Home
        const donutOuter = pxToCtx(28)
        const donutInner = pxToCtx(13)

        ctx.save()
        ctx.fillStyle = '#CE7A58'
        ctx.globalAlpha = 0.18
        ctx.beginPath()
        ctx.arc(base.x, base.y, donutOuter, 0, Math.PI * 2)
        ctx.arc(base.x, base.y, donutInner, 0, Math.PI * 2, true)
        ctx.fill('evenodd')
        ctx.globalAlpha = 1

        ctx.translate(base.x, base.y)
        ctx.rotate(overlay.effectiveYaw - Math.PI / 2)

        const ringPadding = pxToCtx(2)
        const triHalf = pxToCtx(3.5)
        const startX = donutInner + ringPadding
        const endX = donutOuter - ringPadding

        ctx.fillStyle = overlay.isSelected ? '#A85D3D' : '#CE7A58'
        ctx.beginPath()
        ctx.moveTo(endX, 0)
        ctx.lineTo(startX, triHalf)
        ctx.lineTo(startX, -triHalf)
        ctx.closePath()
        ctx.fill()
        ctx.restore()
    }
}

export const drawMarkers = (
    { ctx, width, height, slamToScreenPx, pxToCtx }: DrawContext,
    processedMarkers: Marker[],
    selectedMarkerId: string | null,
    hoveredMarkerId: string | null,
    rotateRef: React.MutableRefObject<{ rotating: boolean; markerId: string; lastYaw: number }>,
    directionOverlayMarkerId: string | null,
    directionOverlayYaw: number | null
) => {
    processedMarkers.forEach((marker) => {
        const base = slamToScreenPx(marker, width, height)
        const isSelected = marker.selected || selectedMarkerId === marker.id
        const isHovered = hoveredMarkerId === marker.id

        ctx.save()
        ctx.fillStyle = isSelected || isHovered ? '#C97B58' : '#D98B64'
        ctx.strokeStyle = isSelected || isHovered ? '#A85D3D' : '#CE7A58'
        ctx.lineWidth = pxToCtx(isSelected || isHovered ? 1.5 : 1.0)

        ctx.beginPath()
        const dotRadius = pxToCtx(isHovered || isSelected ? 5.5 : 4.0)
        ctx.arc(base.x, base.y, dotRadius, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        ctx.restore()
    })

    processedMarkers.forEach((marker) => {
        const isSelected = marker.selected || selectedMarkerId === marker.id
        const isHovered = hoveredMarkerId === marker.id
        if (!isHovered && !isSelected) return

        const base = slamToScreenPx(marker, width, height)
        ctx.save()

        let effectiveYaw = marker.yaw ?? 0
        if (rotateRef.current.rotating && rotateRef.current.markerId === marker.id) {
            effectiveYaw = rotateRef.current.lastYaw
        } else if (
            directionOverlayMarkerId === marker.id &&
            typeof directionOverlayYaw === 'number' &&
            Number.isFinite(directionOverlayYaw)
        ) {
            effectiveYaw = directionOverlayYaw
        }

        const showDirectionOverlay =
            directionOverlayMarkerId === marker.id &&
            typeof effectiveYaw === 'number' &&
            Number.isFinite(effectiveYaw)
        const markerScale = showDirectionOverlay ? 0.8 : 1.0

        const radius = pxToCtx(10.5 * markerScale)
        const pinHeight = pxToCtx(28 * markerScale)
        const gap = pxToCtx(10 * markerScale)

        const cx = base.x
        const cy = base.y - pinHeight + radius - gap
        const donutCenterY = base.y - pxToCtx(14)
        const donutOuter = pxToCtx(32)
        const donutInner = pxToCtx(16)

        if (showDirectionOverlay) {
            ctx.fillStyle = '#CE7A58'
            ctx.globalAlpha = 0.18
            ctx.beginPath()
            ctx.arc(cx, donutCenterY, donutOuter, 0, Math.PI * 2)
            ctx.arc(cx, donutCenterY, donutInner, 0, Math.PI * 2, true)
            ctx.fill('evenodd')
            ctx.globalAlpha = 1
        }

        ctx.fillStyle = isSelected ? '#C97B58' : '#D98B64'
        ctx.strokeStyle = isSelected ? '#A85D3D' : '#CE7A58'
        ctx.lineWidth = pxToCtx(1)
        ctx.lineJoin = 'round'

        ctx.beginPath()
        ctx.arc(cx, cy, radius, Math.PI * 0.15, Math.PI * 0.85, true)
        ctx.quadraticCurveTo(
            cx - radius * 0.9,
            cy + radius * 0.7,
            base.x,
            base.y - gap
        )
        ctx.quadraticCurveTo(
            cx + radius * 0.9,
            cy + radius * 0.7,
            cx + radius * Math.cos(Math.PI * 0.15),
            cy + radius * Math.sin(Math.PI * 0.15)
        )
        ctx.closePath()
        ctx.fill()
        ctx.stroke()

        ctx.fillStyle = '#ffffff'
        ctx.beginPath()
        ctx.arc(cx, cy, radius * 0.35, 0, Math.PI * 2)
        ctx.fill()

        if (showDirectionOverlay) {
            ctx.save()
            ctx.translate(cx, donutCenterY)
            ctx.rotate(effectiveYaw - Math.PI / 2)

            const ringPadding = pxToCtx(2)
            const triHalf = pxToCtx(3.5)
            const startX = donutInner + ringPadding
            const endX = donutOuter - ringPadding

            ctx.fillStyle = isSelected ? '#A85D3D' : '#CE7A58'
            ctx.beginPath()
            ctx.moveTo(endX, 0)
            ctx.lineTo(startX, triHalf)
            ctx.lineTo(startX, -triHalf)
            ctx.closePath()
            ctx.fill()
            ctx.restore()
        }

        ctx.restore()
    })
}

export const drawPendingMarker = (
    { ctx, width, height, slamToScreenPx, pxToCtx }: DrawContext,
    pendingMarker: SlamPoint | null
) => {
    if (!pendingMarker) return
    const base = slamToScreenPx(pendingMarker, width, height)
    const radius = pxToCtx(10.5)
    const pinHeight = pxToCtx(28)
    const gap = pxToCtx(10)
    const cx = base.x
    const cy = base.y - pinHeight + radius - gap

    ctx.save()
    ctx.fillStyle = '#9ca3af'
    ctx.strokeStyle = '#6b7280'
    ctx.lineWidth = pxToCtx(1)
    ctx.globalAlpha = 0.75
    ctx.lineJoin = 'round'

    ctx.beginPath()
    ctx.arc(base.x, base.y, pxToCtx(4.0), 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()

    ctx.beginPath()
    ctx.arc(cx, cy, radius, Math.PI * 0.15, Math.PI * 0.85, true)
    ctx.quadraticCurveTo(
        cx - radius * 0.9,
        cy + radius * 0.7,
        base.x,
        base.y - gap
    )
    ctx.quadraticCurveTo(
        cx + radius * 0.9,
        cy + radius * 0.7,
        cx + radius * Math.cos(Math.PI * 0.15),
        cy + radius * Math.sin(Math.PI * 0.15)
    )
    ctx.closePath()
    ctx.fill()
    ctx.stroke()

    ctx.fillStyle = '#ffffff'
    ctx.beginPath()
    ctx.arc(cx, cy, radius * 0.35, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
}